import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { getDateFromOffset } from '../../../helpers/dates';
import { WelletContext } from '../../../context/WelletContext';
import { skeletonLoading } from '../../../components/Skeletons/SkeletonAccount';
import { useTranslation } from 'react-i18next';
import ContentApp from '../../../components/ContentApp';
import EmptyActivity from '../../../components/Common/EmptyActivity/EmptyActivity';
import AccountHeader from '../../../components/AccountHeader/AccountHeader';
import useRealtimeReservations from '../../../hooks/useRealtimeReservations';
import { CircleSpinner } from 'react-spinners-kit';
import CustomButton from '../../../components/ButtomCustom/CustomButton';
import ArrowIcon from '../../../components/icons/ArrowIcon';
import InstagramIcon2 from '../../../components/icons/InstagramIcon2';
import PaymentBalance from '../../../components/Balance/PaymentBalance';
import PaymentLinkItem from '../../../components/PaymentLinkItem/PaymentLinkItem';
import PaymentLink from '../../../components/icons/PaymentLink';
import IconReserva from '../../../components/icons/IconReserva';

const Home = ({ history }) => {
  const { t } = useTranslation();
  const welletContext = useContext(WelletContext);
  const jwt = useSelector((state) => state.associates.jwt);
  const user = useSelector((state) => state.associates.user);
  const today = getDateFromOffset(user.companyUtcMinutesOffset);
  const todayFilter = format(today, 'yyyy-MM-dd');
  const [loadingBalance, setLoadingBalance] = useState(true);
  const [filterDate, setFilterDate] = useState(
    localStorage.getItem('filterPaymentLink') ?? 'CREATED_DATE',
  );

  const isAssociate = user?.roles?.some((r) =>
    ['associate', 'salesteamlead', 'bosssales'].some((role) => r.includes(role)),
  );

  const [myEarningsToday, setMyEarningsToday] = useState(null);
  const [selectedOption, setSelectedOption] = useState(filterDate);
  const app = useSelector((state) => state.app);

  const getMyEarningsToday = async () => {
    setLoadingBalance(true);
    try {
      const balance = await welletContext.apis.tickets.get(
        `/associates/GetLinkEarnings?from=${todayFilter}&to=${todayFilter}`,
      );
      setMyEarningsToday(balance.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingBalance(false);
    }
  };

  const handleValidReservationUpdate = useCallback(() => {
    getMyEarningsToday();
  }, [getMyEarningsToday]);

  const reservationTypes = {
    ReservationTypeInclude: ['LINK'],
    ReservationTypeExclude: [],
  };

  const { reservations, loading, hasMore, loadingMore } = useRealtimeReservations(
    today,
    filterDate,
    handleValidReservationUpdate,
    reservationTypes,
  );

  useEffect(() => {
    getMyEarningsToday();
  }, [jwt]);

  useEffect(() => {
    const storedFilterDate = localStorage.getItem('filterPaymentLink');
    if (storedFilterDate) {
      setFilterDate(storedFilterDate);
    }
  }, []);

  const isEmptyActivity = reservations?.length === 0 || undefined;

  useEffect(() => {
    if (!localStorage.getItem('filterPaymentLink'))
      localStorage.setItem('filterPaymentLink', 'CREATED_DATE');
  }, []);

  const handleSelectChange = (selectedOption) => {
    localStorage.setItem('filterPaymentLink', selectedOption);
    setFilterDate(selectedOption);
    setSelectedOption(selectedOption);
  };

  return (
    <div>
      <AccountHeader title={app.name} icon={'config'} pathName='/configuration' />
      <ContentApp menu header>
        <div className='mt-2'>
          <PaymentBalance todayBalance={myEarningsToday} />
        </div>

        <div className='d-flex mt-3'>
          {isAssociate && (
            <CustomButton
              size='link'
              text={t('makeNewReservation')}
              leftIcon={<IconReserva size={20} fillColor='#000' />}
              rightIcon={<ArrowIcon size={15} />}
              onClick={() => history.push('/')}
              fontSize='16px'
              className='col mr-1 text-left '
            />
          )}
          <CustomButton
            size='link'
            text={t('createPaymentLink')}
            leftIcon={<PaymentLink size={20} fillColor='#000' />}
            rightIcon={<ArrowIcon size={15} />}
            fontSize='16px'
            onClick={() => history.push('/link-form')}
            className='col ml-1 text-left '
          />
        </div>

        <div className='bold font-normal text-lighter-gray mt-3'>{t('homeLinkTitle')}</div>
        <div className='row mt-3 mb-4'>
          <div className='col pr-1'>
            <div
              className={`account-button ${selectedOption === 'ALL' ? 'selected' : ''}`}
              onClick={() => handleSelectChange('ALL')}
            >
              {t('all')}
            </div>
          </div>
          <div className='col col-md p-0'>
            <div
              className={`account-button ${selectedOption === 'CREATED_DATE' ? 'selected' : ''}`}
              onClick={() => handleSelectChange('CREATED_DATE')}
            >
              {t('mostRecent')}
            </div>
          </div>
          <div className='col pl-1'>
            <div
              className={`account-button ${selectedOption === 'PAID' ? 'selected' : ''}`}
              onClick={() => handleSelectChange('PAID')}
            >
              {t('paid')}
            </div>
          </div>
        </div>

        {loading ? (
          skeletonLoading()
        ) : (
          <>
            {isEmptyActivity ? (
              <EmptyActivity subtitle={<div className='bold'>{t('noBookings')}</div>} />
            ) : (
              <>
                {reservations?.map((r, index) => (
                  <div key={index}>
                    <PaymentLinkItem
                      activity={r}
                      date={r.createdDate}
                      rightText={r.amount}
                      index={index}
                      lastIndex={reservations.length - 1}
                      handleClick={() => history.push(`/link-details?id=${r.id}`)}
                    />
                  </div>
                ))}
                {loadingMore ? (
                  <div className='p-3 d-flex justify-content-center'>
                    <CircleSpinner
                      className='spinner-app mx-auto'
                      style={{ margin: 'auto' }}
                      size={40}
                      color='var(--color-font-primary)'
                    />
                  </div>
                ) : (
                  hasMore && (
                    <div
                      className='text-secondary font-small fw-semibold text-center p-2 mt-2'
                      style={{ border: '1px solid #3D3D3D', borderRadius: '5px' }}
                      onClick={() => {
                        history.push(`/all-payments-link?filter=${selectedOption}`);
                      }}
                    >
                      {t('showAllPaymentsLink')}
                    </div>
                  )
                )}
              </>
            )}
          </>
        )}
      </ContentApp>
    </div>
  );
};

export default Home;
